/**
 * Étape « code reçu par e-mail » de la connexion (24/10/2026, issue #138) : le
 * code part dès l'arrivée sur l'étape, l'adresse masquée dit où le chercher, et
 * « Renvoyer le code » reste à portée. Réservée par le serveur aux comptes sans
 * droit sensible ; sert à la connexion comme à sa mise en place.
 */
import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { ArrowLeft } from 'lucide-react';
import { useAuth, type EmailCodeSent, type LoginStep } from '../../core/auth.js';
import { ApiError } from '../../core/api.js';
import { t } from '../../core/i18n.js';
import { Button, Field, Input } from '../../components/ui.js';
import { Banner } from '../../components/ui/Feedback.js';
import { Switch } from '../../components/ui/Switch.js';

export function EmailCodeStep(props: {
  token: string; trustedDeviceDays?: number | undefined; onBack: () => void;
  /** Étape suivante de la connexion (choix d'entreprise…) quand la session ne s'ouvre pas tout de suite. */
  onStep: (step: LoginStep) => void;
}): ReactNode {
  const { sendEmailCode, verifyEmailCode } = useAuth();
  const [sent, setSent] = useState<EmailCodeSent | undefined>();
  const [code, setCode] = useState('');
  const [rememberDevice, setRememberDevice] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>();
  // Un seul envoi à l'arrivée, même si l'écran se redessine.
  const started = useRef(false);

  const send = async (): Promise<void> => {
    setError(undefined);
    setBusy(true);
    try {
      setSent(await sendEmailCode(props.token));
      setCode('');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('commun.erreurInattendue'));
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    void send();
  });

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    if (!sent) return;
    setError(undefined);
    setBusy(true);
    try {
      // L'appareil n'est retenu que si la personne a vu le choix.
      props.onStep(await verifyEmailCode(props.token, sent.challengeToken, code, rememberDevice && Boolean(props.trustedDeviceDays)));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('commun.erreurInattendue'));
      setBusy(false);
    }
  };

  return (
    // Un code à usage unique n'est pas un mot de passe : rien à enregistrer pour les gestionnaires.
    <form onSubmit={(e) => void onSubmit(e)} className="stack" autoComplete="off">
      {error ? <Banner tone="danger">{error}</Banner> : null}
      <p className="muted" role="status">
        {sent ? t('auth.codeEmail.envoye', { email: sent.emailHint, minutes: Math.max(1, Math.floor(sent.expiresInSeconds / 60)) }) : t('auth.codeEmail.envoi')}
      </p>
      <Field label={t('auth.codeEmail.libelle')} hint={t('auth.codeEmail.aide')}>
        <Input value={code} onChange={(value) => setCode(value.replace(/\D/g, '').slice(0, 6))}
          inputMode="numeric" name="one-time-code" autoComplete="one-time-code" required />
      </Field>
      {props.trustedDeviceDays ? (
        <Switch
          label={t('auth.appareilConfiance', { jours: props.trustedDeviceDays })}
          hint={t('auth.appareilConfianceAide')}
          checked={rememberDevice}
          onChange={setRememberDevice}
        />
      ) : null}
      <Button type="submit" large disabled={busy || !sent || code.length !== 6}>{t('commun.valider')}</Button>
      <Button variant="ghost" disabled={busy} onClick={() => void send()}>{t('auth.codeEmail.renvoyer')}</Button>
      <Button variant="ghost" disabled={busy} onClick={props.onBack}>
        <ArrowLeft size={18} aria-hidden="true" />
        {t('commun.retour')}
      </Button>
    </form>
  );
}
